import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import MainContainer from "../../components/MainContainer";

import {
  selectSelectedTrainModel,
  selectSubmitTrainModelForReviewPending,
  setSelectedCollection,
} from "../../store/slices/model/slice";
import {
  getTrainModel,
  submitTrainModelForReview,
} from "../../store/slices/model/asyncThunks";

import {
  BackButton,
  ModelTitle,
  ConfigSection,
  SectionTitle,
  Label,
  TextInput,
  RequirementsSection,
  InputContainer,
  Row,
  Text,
  SkeletonLoader,
  SkeletonSection,
  SkeletonLoaderColumn,
  List,
  IconContainer,
  SvgContainer,
} from "./TrainingCompletedPage.styles";

import { ReactComponent as ChevronLeft } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as MagicStickSvg } from "../../assets/icons/magicStick.svg";
import { ReactComponent as CircleSvg } from "../../assets/icons/circle.svg";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import Button from "../../components/utils/Button/Button";
import { toast } from "../../components/modern-ui/sonner";
import { TRAIN_MODEL_STATUS } from "../../utils/constants";
import GenerateCollectionModal from "../../components/GenerateCollectionModal/GenerateCollectionModal";

const TrainingCompletedPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();

  const [templateName, setTemplateName] = useState("");
  const [author, setAuthor] = useState("");

  const selectedTrainModel = useSelector(selectSelectedTrainModel);
  const isLoggedIn = useSelector(selectLoggedIn);

  const submitLoading = useSelector(selectSubmitTrainModelForReviewPending);

  const trainModelId = params.modelId;

  const [isGenerateCollectionModalOpen, setIsGenerateCollectionModalOpen] =
    useState(false);

  useEffect(() => {
    if (selectedTrainModel && selectedTrainModel.data) {
      setTemplateName(selectedTrainModel.data.templateName);
      setAuthor(selectedTrainModel.data.author);
    }
  }, [selectedTrainModel]);

  useEffect(() => {
    if (isLoggedIn && trainModelId) {
      dispatch(getTrainModel(trainModelId));
    }
  }, [trainModelId, isLoggedIn, dispatch]);

  const handleBack = () => {
    navigate("/train");
  };

  const actionSubmitForReview = () => {
    dispatch(submitTrainModelForReview({ trainModelId }))
      .unwrap()
      .then(() => {
        navigate(`/train/completed/${trainModelId}`);
      })
      .catch(() => {
        toast.error(`Error occurred while submitting model for review`);
      });
  };

  const toCamelCase = (str) => {
    return str
      .toLowerCase()
      .split("_")
      .map((word, index) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleGenerateAgain = () => {
    const modelObject = {
      ...selectedTrainModel.data?.selectedCollection,
      regenerate: false,
      collectionId: selectedTrainModel.data?.selectedCollection._id,
    };

    dispatch(setSelectedCollection(modelObject));
    setIsGenerateCollectionModalOpen(true);
  };

  // Show loading state
  if (selectedTrainModel.loading) {
    return (
      <MainContainer>
        <BackButton onClick={handleBack}>
          <ChevronLeft />
          Back to Train
        </BackButton>
        <SkeletonLoaderColumn>
          <SkeletonLoader $height="26px" $width="200px" $radius="4px" />
          <IconContainer>
            <SkeletonLoader $height="72px" $width="72px" $radius="50%" />
            <SkeletonLoader $height="22px" $width="180px" />
          </IconContainer>
          <Row>
            <SkeletonSection $height="335px">
              <SkeletonLoader $height="22px" $width="120px" />
              <SkeletonLoader $height="16px" $width="150px" />
              <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
              <SkeletonLoader $height="16px" $width="80px" />
              <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
              <SkeletonLoader $height="44px" $width="100%" $radius="100px" />
            </SkeletonSection>

            <SkeletonSection $height="335px" style={{ gap: 20 }}>
              <SkeletonLoader $height="22px" $width="180px" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="16px" $width="100%" />
            </SkeletonSection>
          </Row>
          <SkeletonLoader
            $height="44px"
            $width="128px"
            $radius="100px"
            style={{ alignSelf: "flex-end" }}
          />
        </SkeletonLoaderColumn>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <BackButton onClick={handleBack}>
        <ChevronLeft />
        Back to Train
      </BackButton>

      <ModelTitle>{templateName}</ModelTitle>
      <IconContainer>
        <SvgContainer>
          <CircleSvg />
        </SvgContainer>

        <SectionTitle>Training Completed!</SectionTitle>
      </IconContainer>

      <Row>
        <ConfigSection>
          <SectionTitle>Publish</SectionTitle>

          <InputContainer>
            <Label>Template name</Label>
            <TextInput
              disabled={true}
              type="text"
              placeholder="New Model 01"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </InputContainer>

          <InputContainer>
            <Label>Author</Label>
            <TextInput
              disabled={true}
              type="text"
              placeholder="@author"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
            />
          </InputContainer>
          {selectedTrainModel.data.status === TRAIN_MODEL_STATUS.COMPLETED ? (
            <Button
              loading={submitLoading}
              title={"Submit for Review"}
              onClick={actionSubmitForReview}
              style={{ width: "100%" }}
            />
          ) : (
            <Text>
              Train model status: {toCamelCase(selectedTrainModel.data.status)}
            </Text>
          )}
        </ConfigSection>

        <RequirementsSection>
          <SectionTitle>Review Process</SectionTitle>

          <List>
            <Text>
              Template must pass our team’s review before becoming publicly
              available.
            </Text>
            <Text>
              You’ll earn 2 credit for each user generation using your template.
            </Text>
            <Text>
              We review for quality standards and potential copyright
              infringement.
            </Text>
            <Text>
              Template approval is not guaranteed and we reserve final
              discretion.
            </Text>
            <Text>Once published, templates become permanently available.</Text>
          </List>
        </RequirementsSection>
      </Row>
      {selectedTrainModel.data?.selectedCollection && (
        <Button
          leftIcon={<MagicStickSvg />}
          title={"Use Your Model"}
          onClick={handleGenerateAgain}
          style={{ alignSelf: "flex-end" }}
        />
      )}
      <GenerateCollectionModal
        isOpen={isGenerateCollectionModalOpen}
        onClose={() => setIsGenerateCollectionModalOpen(false)}
      />
    </MainContainer>
  );
};

export default TrainingCompletedPage;
